import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { m } from 'motion/react';
import { Inbox } from 'lucide-react';

import { ApprovalCard } from './ApprovalCard';
import { approvals, type Approval } from '../../api/tasks';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorBanner } from '../../components/ErrorBanner';
import { SkeletonRows } from '../../components/Skeleton';
import { PageHeader } from '../../components/Surface';
import { RISE, STAGGER } from '../../components/motion';
import { useT } from '../../i18n/provider';
import { useAuth } from '../../stores/auth';

/**
 * La bandeja de aprobaciones de `13 §3.8`, de todas las empresas de quien
 * llama.
 *
 * La aprobación en lote solo se ofrece para lo que el backend marcó como
 * `bulk_approvable` —ya excluyó todo lo que gasta dinero, amplía permisos o
 * escribe en `main`— y la selección es por tarjeta y no un «aprobar todo»,
 * porque un montón de compuertas rutinarias con una fusión en medio es
 * exactamente la situación para la que existe la bandera.
 */
export function ApprovalsScreen() {
  const t = useT();
  const queryClient = useQueryClient();
  const companies = useAuth((state) => state.user?.companies ?? []);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const inbox = useQuery({
    queryKey: ['approvals', 'inbox'],
    queryFn: () => approvals.inbox(),
    refetchInterval: 15_000,
    staleTime: 0,
  });

  const refresh = () => {
    setSelected(new Set());
    void queryClient.invalidateQueries({ queryKey: ['approvals'] });
  };

  const bulk = useMutation({
    mutationFn: () => approvals.bulkApprove([...selected]),
    onSuccess: refresh,
  });

  if (inbox.isPending) return <SkeletonRows rows={3} />;
  if (inbox.error !== null) return <ErrorBanner error={inbox.error} onRetry={() => void inbox.refetch()} />;

  const rows = inbox.data?.data ?? [];
  const companyName = (id: string) => companies.find((company) => company.id === id)?.name ?? t('approvals.unknownCompany');

  const toggle = (approval: Approval, checked: boolean) => {
    setSelected((current) => {
      const next = new Set(current);

      if (checked) next.add(approval.id);
      else next.delete(approval.id);

      return next;
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('approvals.title', { count: rows.length })}
        description={t('approvals.intro')}
        actions={
          selected.size > 0 ? (
            <Button busy={bulk.isPending} onClick={() => bulk.mutate()}>
              {t('approvals.approveSelected', { count: selected.size })}
            </Button>
          ) : null
        }
      />

      {bulk.error !== null ? <ErrorBanner error={bulk.error} /> : null}

      {rows.length === 0 ? (
        <EmptyState
          icon={<Inbox />}
          title={t('approvals.nothingTitle')}
          description={t('approvals.nothingHint')}
        />
      ) : (
        <m.ul variants={STAGGER} initial="hidden" animate="shown" className="space-y-4">
          {rows.map((approval) => (
            <m.li key={approval.id} variants={RISE} layout>
              <ApprovalCard
                approval={approval}
                companyName={companyName(approval.company_id)}
                selected={selected.has(approval.id)}
                onSelect={(checked) => toggle(approval, checked)}
                onDecided={refresh}
              />
            </m.li>
          ))}
        </m.ul>
      )}
    </div>
  );
}
